import { CommentStatusEnum } from '@api/enum';
import { getCameraImageCommentsActivity } from '@api/services/analytics.service';
import useDataQuery from '@hooks/use-data-query';
import { useCallback, useMemo, useState } from 'react';

const useCommentsActivityListData = (startDate: string, endDate: string, initialFilter: string = 'ALL') => {
    const [filterValue, setFilterValue] = useState(initialFilter);

    const changeFilterValue = useCallback((val: string | number) => {
        setFilterValue(val as CommentStatusEnum | 'ALL');
    }, []);

    const commentsQuery = useDataQuery(['comment-activity', startDate, endDate], () =>
        getCameraImageCommentsActivity(startDate, endDate)
    );

    const allComments = commentsQuery.data || [];

    const filteredComments = useMemo(() => {
        if (commentsQuery.isLoading) return [];
        return filterValue === 'ALL'
            ? allComments
            : allComments.filter((comment: { status: string }) => comment.status === filterValue);
    }, [commentsQuery.isLoading, filterValue, allComments]);

    const statusCounts = useMemo(() => {
        const counts: Record<CommentStatusEnum, number> = {
            [CommentStatusEnum.OPEN]: 0,
            [CommentStatusEnum.CLOSED]: 0,
        };

        for (const comment of allComments) {
            const status = comment.status as CommentStatusEnum;
            if (status in counts) {
                counts[status]++;
            }
        }

        return {
            ...counts,
            ALL: allComments.length,
        };
    }, [allComments]);

    return {
        data: filteredComments,
        isLoading: commentsQuery.isLoading,
        filterValue,
        changeFilterValue,
        statusCounts,
    };
};

export default useCommentsActivityListData;
